/**
 * PURPOSE: Editor de la política de capacidades SISDEU de la compañía (FTR-0017 Phase 4, D3).
 * RESPONSIBILITIES: Leer el catálogo real de capacidades ya usado por "Accesos" (nunca inventa cadenas
 *   nuevas), mostrar el estado actual habilitado/deshabilitado por capacidad, explicar que la política
 *   solo rige cuentas SISDEU sin excepción individual, calcular con datos ya cargados (sin endpoint de
 *   vista previa) cuántas cuentas SISDEU de la compañía la siguen hoy, exigir confirmación mostrando ese
 *   impacto antes de aplicar, y mostrar el conteo exacto que el backend devuelve después de aplicar.
 * COLLABORATORS: sisdeuAdminService.js, sisdeuAdmin.utils.js, portalAccounts.utils.js (catálogo de
 *   capacidades compartido), portalAccountService.js (listado de cuentas para el impacto), effective
 *   company (cabecera X-Hayllis-Company ya activa vía httpService), CompanyInstitutionsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/SisdeuPolicyModal.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { listPortalAccounts } from "../../services/portalAccountService.js";
import { getSisdeuPolicy, updateSisdeuPolicy } from "../../services/sisdeuAdminService.js";
import { PORTAL_CAPABILITY_LABELS, PORTAL_CAPABILITY_OPTIONS } from "./portalAccounts.utils.js";
import {
  computePolicyChangeImpact,
  getSisdeuAdminErrorMessage,
  isCompanyAdminForbiddenError,
  isDelegatePortalUnavailableError,
  isSisdeuCompanyDisabledError,
} from "./sisdeuAdmin.utils.js";

const SisdeuPolicyModal = ({ onClose }) => {
  const [policy, setPolicy] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: "disabled"|"forbidden"|"error", message }

  const load = useCallback(async () => {
    setLoading(true);
    setNotice(null);
    try {
      const [policyRows, accountRows] = await Promise.all([getSisdeuPolicy(), listPortalAccounts()]);
      setPolicy(policyRows);
      setAccounts(accountRows);
      setEnabled(new Set(policyRows.filter((row) => row.enabled).map((row) => row.capability)));
    } catch (error) {
      if (isSisdeuCompanyDisabledError(error)) {
        setNotice({ tone: "disabled", message: getSisdeuAdminErrorMessage(error, "") });
      } else if (isCompanyAdminForbiddenError(error) || isDelegatePortalUnavailableError(error)) {
        setNotice({ tone: "forbidden", message: getSisdeuAdminErrorMessage(error, "No tienes acceso a esta sección.") });
      } else {
        setNotice({ tone: "error", message: getSisdeuAdminErrorMessage(error, "No se pudo cargar la política SISDEU.") });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (capability) =>
    setEnabled((current) => {
      const next = new Set(current);
      if (next.has(capability)) next.delete(capability);
      else next.add(capability);
      return next;
    });

  const handleSave = async () => {
    const nextEnabled = Array.from(enabled);
    const impact = computePolicyChangeImpact(policy, nextEnabled, accounts);
    if (impact.changedCapabilities.length === 0) {
      toast("No hay cambios para guardar.");
      return;
    }
    const changedLabels = impact.changedCapabilities.map((capability) => PORTAL_CAPABILITY_LABELS[capability] || capability);
    const confirmed = window.confirm(
      `¿Aplicar este cambio de política SISDEU?\n\nCapacidades que cambian: ${changedLabels.join(", ")}.\n` +
        `Afecta solo a cuentas SISDEU que siguen la política de la compañía (hoy: ${impact.policyFollowingSisdeuAccountCount}). ` +
        `Una cuenta con una excepción individual no cambia.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const result = await updateSisdeuPolicy(nextEnabled);
      setPolicy(result.capabilities);
      setEnabled(new Set(result.capabilities.filter((row) => row.enabled).map((row) => row.capability)));
      toast.success(`Política actualizada. ${result.affectedAccounts} cuenta(s) SISDEU actualizada(s).`);
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo actualizar la política."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sisdeu-policy-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 id="sisdeu-policy-title" className="text-xl font-bold text-slate-800">
              Política de capacidades SISDEU
            </h3>
            <p className="text-sm text-slate-500">
              Aplica por defecto a las cuentas SISDEU nuevas y a las que aún no tienen una excepción individual.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
        ) : notice ? (
          <div
            className={`rounded-lg border p-4 text-sm ${
              notice.tone === "disabled"
                ? "border-slate-200 bg-slate-50 text-slate-600"
                : "border-amber-200 bg-amber-50 text-amber-800"
            }`}
          >
            {notice.message}
          </div>
        ) : (
          <div className="space-y-4">
            <fieldset className="space-y-2 rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-sm font-medium text-slate-700">Capacidades</legend>
              {PORTAL_CAPABILITY_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center justify-between gap-3 text-sm text-slate-700">
                  <span>{option.label}</span>
                  <input
                    type="checkbox"
                    checked={enabled.has(option.value)}
                    onChange={() => toggle(option.value)}
                    disabled={saving}
                  />
                </label>
              ))}
            </fieldset>
            <p className="text-xs text-slate-500">
              Cuentas SISDEU de esta compañía que hoy siguen la política:{" "}
              <strong>{computePolicyChangeImpact(policy, Array.from(enabled), accounts).policyFollowingSisdeuAccountCount}</strong>.
              Una cuenta con una excepción individual (en "Accesos") no se ve afectada por este cambio.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Guardando..." : "Guardar política"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SisdeuPolicyModal;
